/**
 * Networking: a thin event-emitting wrapper around the Colyseus 0.17 SDK plus
 * REST helpers for the lobby/matchmaking endpoints.
 */
import { Client, type Room } from '@colyseus/sdk';
import { CONFIG } from './config.js';
import { getUserId, getPrefs } from './storage.js';

export interface RoomPlayer {
  sessionId: string;
  userId: string;
  name: string;
  colorIndex: number;
  ballX: number;
  ballY: number;
  strokesThisHole: number;
  strokesTotal: number;
  holed: boolean;
  connected: boolean;
  isBot: boolean;
  holeScores: number[];
}

export interface RoomStateView {
  phase: string;
  currentTurn: string;
  holeIndex: number;
  holeCount: number;
  par: number;
  maxStrokes: number;
  trackId: string;
  trackName: string;
  turnDeadline: number;
  turnTimeoutMs: number;
  collisionEnabled: boolean;
  players: RoomPlayer[];
  chat: Array<{ id: string; sessionId: string; name: string; text: string; ts: number; system: boolean }>;
}

type Handler = (payload: unknown) => void;

/** sessionStorage key holding `{ token, ts }` for resuming a live match. */
const RECONNECT_KEY = 'jgolf_reconnect';
/** Server holds a disconnected seat for 300s; only resume within that window. */
const RECONNECT_MAX_AGE_MS = 290_000;

export class GameNet {
  private client: Client;
  private room: Room | null = null;
  private handlers = new Map<string, Set<Handler>>();
  private reconnecting = false;
  /** Set when the user deliberately leaves so we don't try to resume. */
  private intentionalLeave = false;
  sessionId = '';
  isSpectator = false;
  /** Cached so the GameScene can read the first hole that arrived during join. */
  lastHole: unknown = null;

  constructor() {
    this.client = new Client(CONFIG.COLYSEUS_URL);
  }

  on(event: string, fn: Handler): () => void {
    let set = this.handlers.get(event);
    if (!set) {
      set = new Set();
      this.handlers.set(event, set);
    }
    set.add(fn);
    return () => set?.delete(fn);
  }

  private emit(event: string, payload: unknown): void {
    this.handlers.get(event)?.forEach((fn) => fn(payload));
  }

  async joinById(roomId: string, opts: { spectate?: boolean } = {}): Promise<void> {
    const prefs = getPrefs();
    const room = await this.client.joinById(roomId, {
      name: prefs.nickname,
      userId: getUserId(),
      token: prefs.secretToken,
      spectate: opts.spectate,
    });
    this.isSpectator = !!opts.spectate;
    this.bind(room);
  }

  /** True while a room connection exists (even if momentarily dropped). */
  get inRoom(): boolean {
    return this.room !== null;
  }

  /** True when the last leave was user-initiated (don't show error UI). */
  get leftIntentionally(): boolean {
    return this.intentionalLeave;
  }

  // ── Reconnection ─────────────────────────────────────────────
  private saveReconnectToken(): void {
    if (this.isSpectator || !this.room?.reconnectionToken) return;
    try {
      sessionStorage.setItem(RECONNECT_KEY, JSON.stringify({ token: this.room.reconnectionToken, ts: Date.now() }));
    } catch {
      /* ignore */
    }
  }

  private clearReconnectToken(): void {
    try {
      sessionStorage.removeItem(RECONNECT_KEY);
    } catch {
      /* ignore */
    }
  }

  /** A stored reconnection token fresh enough for the server's 300s grace. */
  static storedReconnectToken(): string | null {
    try {
      const raw = sessionStorage.getItem(RECONNECT_KEY);
      if (!raw) return null;
      const { token, ts } = JSON.parse(raw) as { token: string; ts: number };
      if (!token || Date.now() - ts > RECONNECT_MAX_AGE_MS) return null;
      return token;
    } catch {
      return null;
    }
  }

  /** Resume a dropped seat via the Colyseus reconnection token. */
  async reconnect(token: string): Promise<void> {
    const room = await this.client.reconnect(token);
    this.isSpectator = false;
    this.bind(room);
    // The server pushes hole_data the instant the seat reactivates, which can
    // race our handler registration — ask again now that we're listening.
    this.send('request_hole');
  }

  /**
   * After an unintended disconnect, retry `client.reconnect()` with backoff
   * while the server still holds our seat. Emits `reconnected` on success,
   * `reconnect_failed` when we give up.
   */
  private async tryResume(): Promise<void> {
    if (this.reconnecting || this.intentionalLeave || this.isSpectator) return;
    const stored = GameNet.storedReconnectToken();
    if (!stored) {
      this.emit('reconnect_failed', {});
      return;
    }
    this.reconnecting = true;
    this.emit('reconnecting', {});
    const deadline = Date.now() + 60_000;
    let delay = 1000;
    while (Date.now() < deadline) {
      // Skip attempts while hidden — the browser throttles us anyway and the
      // visibilitychange fast-path below retries the moment we're back.
      if (!document.hidden) {
        try {
          await this.reconnect(stored);
          this.reconnecting = false;
          this.emit('reconnected', {});
          return;
        } catch {
          /* seat may still be reachable — retry until the deadline */
        }
      }
      await new Promise((r) => setTimeout(r, delay));
      delay = Math.min(delay * 1.5, 5000);
    }
    this.reconnecting = false;
    this.clearReconnectToken();
    this.emit('reconnect_failed', {});
  }

  /** Fast-path retry when the tab becomes visible again with a dead room. */
  onVisible(): void {
    if (!this.room && !this.reconnecting && !this.intentionalLeave && GameNet.storedReconnectToken()) {
      void this.tryResume();
    }
  }

  private bind(room: Room): void {
    this.room = room;
    this.sessionId = room.sessionId;
    this.intentionalLeave = false;
    this.saveReconnectToken();
    room.onStateChange((state: unknown) => this.emit('state', this.snapshot(state)));
    const events = [
      'hole_data',
      'spectating',
      'game_start',
      'turn',
      'turn_skipped',
      'stroke_result',
      'hole_complete',
      'game_over',
      'player_joined',
      'player_disconnected',
      'player_reconnected',
      'emote',
      'rematch_vote',
    ];
    for (const ev of events) {
      room.onMessage(ev, (payload) => {
        if (ev === 'hole_data') this.lastHole = payload;
        this.emit(ev, payload);
      });
    }
    // Transport-level blips: the SDK retries the same connection seamlessly.
    room.onDrop(() => this.emit('reconnecting', {}));
    room.onReconnect(() => {
      this.saveReconnectToken();
      this.send('request_hole');
      this.emit('reconnected', {});
    });
    room.onLeave((code) => {
      this.room = null;
      this.emit('left', { code });
      const consented = code === 1000 || this.intentionalLeave;
      if (consented || this.isSpectator) {
        this.clearReconnectToken();
      } else {
        // Abnormal close (network loss, backgrounded mobile tab, server blip):
        // the server holds our seat for 300s — try to take it back.
        void this.tryResume();
      }
    });
    room.onError((code, message) => this.emit('error', { code, message }));
  }

  private snapshot(state: unknown): RoomStateView {
    const s = state as Record<string, unknown> & { players?: { forEach: (cb: (p: unknown) => void) => void }; chat?: Array<unknown> };
    const players: RoomPlayer[] = [];
    s.players?.forEach((p: unknown) => {
      const pl = p as Record<string, unknown>;
      players.push({
        sessionId: String(pl.sessionId),
        userId: String(pl.userId),
        name: String(pl.name),
        colorIndex: Number(pl.colorIndex),
        ballX: Number(pl.ballX),
        ballY: Number(pl.ballY),
        strokesThisHole: Number(pl.strokesThisHole),
        strokesTotal: Number(pl.strokesTotal),
        holed: Boolean(pl.holed),
        connected: Boolean(pl.connected),
        isBot: Boolean(pl.isBot),
        holeScores: Array.from((pl.holeScores as number[]) ?? []),
      });
    });
    const chat = Array.from((s.chat as Array<Record<string, unknown>>) ?? []).map((c) => ({
      id: String(c.id),
      sessionId: String(c.sessionId ?? ''),
      name: String(c.name),
      text: String(c.text),
      ts: Number(c.ts),
      system: Boolean(c.system),
    }));
    return {
      phase: String(s.phase ?? 'lobby'),
      currentTurn: String(s.currentTurn ?? ''),
      holeIndex: Number(s.holeIndex ?? 0),
      holeCount: Number(s.holeCount ?? 1),
      par: Number(s.par ?? 3),
      maxStrokes: Number(s.maxStrokes ?? 0),
      trackId: String(s.trackId ?? ''),
      trackName: String(s.trackName ?? ''),
      turnDeadline: Number(s.turnDeadline ?? 0),
      turnTimeoutMs: Number(s.turnTimeoutMs ?? 0),
      collisionEnabled: Boolean(s.collisionEnabled),
      players,
      chat,
    };
  }

  send(type: string, payload?: unknown): void {
    this.room?.send(type, payload);
  }

  leave(): void {
    this.intentionalLeave = true;
    this.clearReconnectToken();
    try {
      this.room?.leave(true);
    } catch {
      /* ignore */
    }
    this.room = null;
  }
}

// ── REST helpers ─────────────────────────────────────────────
async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${CONFIG.API_BASE}/api${path}`, {
    headers: { 'Content-Type': 'application/json' },
    ...init,
  });
  if (!res.ok) throw new Error(`API ${path} ${res.status}`);
  return (await res.json()) as T;
}

export interface CreateMatchOpts {
  vsBot?: boolean;
  botDifficulty?: string;
  holeCount?: number;
  maxStrokes?: number;
  turnTimeoutMs?: number;
  expectedPlayers?: number;
  collision?: boolean;
}

export const Rest = {
  createMatch(opts: CreateMatchOpts): Promise<{ roomId: string; code: string }> {
    return api('/match/create', { method: 'POST', body: JSON.stringify({ hostName: getPrefs().nickname, ...opts }) });
  },
  matchByCode(code: string): Promise<{ roomId: string; code: string }> {
    return api(`/match/by-code/${encodeURIComponent(code)}`);
  },
  listMatches(): Promise<{ matches: Array<{ roomId: string; code: string; hostName: string; holeCount: number; playerCount: number }> }> {
    return api('/match/list');
  },
  activeGames(): Promise<{ games: Array<{ roomId: string; hostName: string; playerCount: number }> }> {
    return api('/match/active');
  },
  heartbeat(status?: 'online' | 'away' | 'in-game', keepalive = false): Promise<{ ok: boolean; warning?: string }> {
    const p = getPrefs();
    const effective = status ?? (document.hidden ? 'away' : 'online');
    return api('/lobby/heartbeat', {
      method: 'POST',
      keepalive,
      body: JSON.stringify({ userId: getUserId(), name: p.nickname, token: p.secretToken, status: effective }),
    });
  },
  users(): Promise<{ users: Array<{ id: string; name: string; status: string; isBot: boolean }> }> {
    return api('/lobby/users');
  },
  leave(): Promise<{ ok: boolean }> {
    return api('/lobby/leave', { method: 'POST', keepalive: true, body: JSON.stringify({ userId: getUserId() }) });
  },
  /** Fire-and-forget leave for pagehide/unload (sendBeacon survives tab close). */
  leaveBeacon(): void {
    const body = JSON.stringify({ userId: getUserId() });
    try {
      if (!navigator.sendBeacon(`${CONFIG.API_BASE}/api/lobby/leave`, new Blob([body], { type: 'application/json' }))) {
        void fetch(`${CONFIG.API_BASE}/api/lobby/leave`, { method: 'POST', keepalive: true, headers: { 'Content-Type': 'application/json' }, body }).catch(() => {});
      }
    } catch {
      void fetch(`${CONFIG.API_BASE}/api/lobby/leave`, { method: 'POST', keepalive: true, headers: { 'Content-Type': 'application/json' }, body }).catch(() => {});
    }
  },
  challenge(toId: string, toName: string): Promise<{ ok: boolean; challengeId: string; botGame?: boolean }> {
    return api('/lobby/challenge', { method: 'POST', body: JSON.stringify({ fromId: getUserId(), fromName: getPrefs().nickname, toId, toName }) });
  },
  getChallenges(): Promise<{ incoming: Array<{ id: string; fromName: string; fromId: string }>; outgoing: Array<{ id: string; status: string; roomId: string | null }> }> {
    return api(`/lobby/challenges?userId=${encodeURIComponent(getUserId())}`);
  },
  respondChallenge(id: string, accept: boolean, roomId?: string): Promise<{ ok: boolean }> {
    return api(`/lobby/challenge/${id}/respond`, { method: 'POST', body: JSON.stringify({ accept, roomId, userId: getUserId() }) });
  },
  messages(after: number): Promise<{ messages: Array<{ id: string; name: string; text: string; ts: number; isSystem?: boolean }> }> {
    return api(`/lobby/messages?after=${after}`);
  },
  postMessage(text: string): Promise<{ ok: boolean }> {
    return api('/lobby/message', { method: 'POST', body: JSON.stringify({ name: getPrefs().nickname, text }) });
  },
  leaderboard(): Promise<{ leaderboard: Array<{ nickname: string; gamesWon: number; gamesPlayed: number }> }> {
    return api('/leaderboard');
  },
  log(level: string, message: string, data?: unknown): void {
    void api('/logs/client', { method: 'POST', body: JSON.stringify({ logs: [{ level, message, data, ts: Date.now() }] }) }).catch(() => {});
  },
};

export const net = new GameNet();
